import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { LOGO_URL } from "@/lib/brand";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [li, setLi] = useState({ identifier: "", password: "" });
  const [reg, setReg] = useState({ email: "", password: "", name: "", company: "" });

  const doLogin = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    const r = await login(li.identifier, li.password);
    setLoading(false);
    if (r.ok) navigate("/app"); else setError(r.error);
  };
  const doRegister = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    const r = await register(reg);
    setLoading(false);
    if (r.ok) navigate("/app"); else setError(r.error);
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden overflow-hidden bg-card lg:block grain">
        <div className="pointer-events-none absolute -bottom-40 -left-20 h-96 w-96 rounded-full bg-gold-500/20 blur-[120px]" />
        <div className="relative flex h-full flex-col justify-between p-12">
          <Link to="/" className="flex items-center gap-2.5">
            <img src={LOGO_URL} alt="CRÉRÉA" className="h-10 w-10 rounded-lg object-contain ring-1 ring-gold-500/30" />
            <span className="font-display text-lg font-bold tracking-tight">CRÉRÉA</span>
          </Link>
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-gold-400">Passerelle IA · Une clé, tous les modèles</p>
            <h2 className="mt-3 font-display text-3xl font-bold leading-tight tracking-tight">
              « Un seul endpoint. Tous les modèles frontier du monde. »
            </h2>
            <p className="mt-4 max-w-md text-muted-foreground">Connectez-vous pour générer texte, image et vidéo dans le Studio, gérer vos clés API et vos crédits.</p>
          </div>
          <div className="flex gap-8 text-sm text-muted-foreground">
            <span><span className="font-display text-2xl font-bold text-gold-400">1 clé</span><br />Tous les modèles</span>
            <span><span className="font-display text-2xl font-bold text-gold-400">100%</span><br />Compatible OpenAI</span>
            <span><span className="font-display text-2xl font-bold text-gold-400">0€</span><br />Sans abonnement</span>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center bg-background p-6">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <Button variant="ghost" size="sm" onClick={() => navigate("/")} className="mb-6 -ml-2 text-muted-foreground" data-testid="back-home-btn">
            <ArrowLeft className="mr-1 h-4 w-4" /> Accueil
          </Button>
          <h1 className="font-display text-2xl font-bold tracking-tight">Accédez à CRÉRÉA</h1>
          <p className="mt-1 text-sm text-muted-foreground">Compte démo admin : <span className="font-mono text-foreground">créréa06 / créréa+06</span></p>

          <Tabs defaultValue="login" className="mt-6">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login" data-testid="tab-login">Connexion</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Créer un compte</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={doLogin} className="space-y-4">
                <div>
                  <Label htmlFor="li-id">Identifiant ou email</Label>
                  <Input id="li-id" data-testid="login-identifier" value={li.identifier} onChange={(e) => setLi({ ...li, identifier: e.target.value })} placeholder="créréa06" className="mt-1.5" required />
                </div>
                <div>
                  <Label htmlFor="li-pw">Mot de passe</Label>
                  <Input id="li-pw" data-testid="login-password" type="password" value={li.password} onChange={(e) => setLi({ ...li, password: e.target.value })} placeholder="••••••••" className="mt-1.5" required />
                </div>
                {error && <p data-testid="auth-error" className="text-sm text-red-400">{error}</p>}
                <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full rounded-full bg-gold-500 text-black hover:bg-gold-400">
                  {loading ? "Connexion..." : "Se connecter"}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={doRegister} className="space-y-4">
                <div>
                  <Label htmlFor="r-name">Nom</Label>
                  <Input id="r-name" data-testid="register-name" value={reg.name} onChange={(e) => setReg({ ...reg, name: e.target.value })} placeholder="Jean Dupont" className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="r-company">Entreprise</Label>
                  <Input id="r-company" data-testid="register-company" value={reg.company} onChange={(e) => setReg({ ...reg, company: e.target.value })} placeholder="Plomberie Dupont" className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="r-email">Email</Label>
                  <Input id="r-email" data-testid="register-email" type="email" value={reg.email} onChange={(e) => setReg({ ...reg, email: e.target.value })} placeholder="jean@exemple.fr" className="mt-1.5" required />
                </div>
                <div>
                  <Label htmlFor="r-pw">Mot de passe</Label>
                  <Input id="r-pw" data-testid="register-password" type="password" value={reg.password} onChange={(e) => setReg({ ...reg, password: e.target.value })} placeholder="••••••••" className="mt-1.5" required />
                </div>
                {error && <p data-testid="auth-error" className="text-sm text-red-400">{error}</p>}
                <Button type="submit" disabled={loading} data-testid="register-submit" className="w-full rounded-full bg-gold-500 text-black hover:bg-gold-400">
                  {loading ? "Création..." : "Créer mon compte"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>
    </div>
  );
}
